/**
 * prefetch.js - Enhanced prefetch with multiple strategies
 *
 * Supports multiple prefetch modes:
 * - hover: Prefetch on mouseenter (default)
 * - hover:300: Prefetch after 300ms hover delay
 * - visible: Prefetch when element scrolls into view
 * - eager: Prefetch immediately on page load
 * - false: Disable prefetching
 *
 * Prefetch is opt-in via data-prefetch or JSON config.
 */

import { resolve } from './cascade.js';
import { getURL, shouldSkip, getMethod } from './request.js';
import { setInCache, getFromCache, parseDuration } from './cache.js';

const INIT_KEY = '_htmlstarPrefetch';

/**
 * Set of URLs currently being prefetched (to avoid duplicate requests)
 */
const prefetching = new Set();

/**
 * Lazy-initialized IntersectionObserver for visible mode
 */
let visibleObserver;

function getVisibleObserver() {
  if (!visibleObserver) {
    visibleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          prefetchElement(entry.target);
          visibleObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '100px' });
  }
  return visibleObserver;
}

/**
 * Parse prefetch mode from attribute value
 *
 * @param {string} value - e.g., "hover", "hover:300", "visible", "eager", "false"
 * @returns {{ mode: string, delay: number }}
 */
function parsePrefetchMode(value) {
  if (value === null || value === undefined) return { mode: 'none', delay: 0 };
  if (value === '' || value === 'true') return { mode: 'hover', delay: 0 };
  if (value === 'false') return { mode: 'none', delay: 0 };

  const [mode, delayStr] = value.split(':');
  const delay = delayStr ? parseInt(delayStr, 10) : 0;

  return { mode, delay };
}

/**
 * Prefetch the URL for an element
 *
 * @param {HTMLElement} el
 */
function prefetchElement(el) {
  const url = getURL(el);
  if (!url) return;

  // Only prefetch GET requests
  if (getMethod(el) !== 'GET') return;

  // Check if should skip (external, download, etc.)
  if (shouldSkip(el, url)) return;

  // Determine TTL (default 5 minutes for prefetch)
  const cacheConfig = resolve(el, 'cache') || '5m';
  const ttl = parseDuration(cacheConfig);

  if (ttl > 0) {
    prefetchURL(url, ttl);
  }
}

/**
 * Prefetch a URL and store in cache
 *
 * @param {string} url
 * @param {number} ttl - Cache TTL in milliseconds
 * @returns {Promise<void>}
 */
async function prefetchURL(url, ttl) {
  // Skip if already in cache
  if (getFromCache(url, 'GET')) return;

  // Skip if already prefetching
  if (prefetching.has(url)) return;

  prefetching.add(url);

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'htmlstar',
        'Purpose': 'prefetch'
      },
      priority: 'low'
    });

    if (res.ok) {
      const html = await res.text();
      setInCache(url, 'GET', html, ttl);
    }
  } catch {
    // Silently fail - prefetch is best-effort
  } finally {
    prefetching.delete(url);
  }
}

/**
 * Initialize prefetch listeners on elements
 *
 * @param {HTMLElement|Document} root
 */
export function initPrefetch(root = document) {
  // Find elements that could trigger requests
  const selector = 'a[href], [data-href]';
  const elements = root === document
    ? document.querySelectorAll(selector)
    : root.matches?.(selector)
      ? [root, ...root.querySelectorAll(selector)]
      : root.querySelectorAll(selector);

  for (const el of elements) {
    // Skip if already initialized
    if (el[INIT_KEY]) continue;

    // Get prefetch config via cascade
    const prefetchConfig = resolve(el, 'prefetch');
    const { mode, delay } = parsePrefetchMode(prefetchConfig);

    if (mode === 'none') continue;
    el[INIT_KEY] = true;

    if (mode === 'visible') {
      getVisibleObserver().observe(el);
    } else if (mode === 'eager') {
      // Prefetch immediately (next tick to allow init to finish)
      setTimeout(() => prefetchElement(el), 0);
    } else if (mode === 'hover') {
      // Hover with optional delay
      if (delay > 0) {
        let timeout;
        el.addEventListener('mouseenter', () => {
          timeout = setTimeout(() => prefetchElement(el), delay);
        });
        el.addEventListener('mouseleave', () => {
          clearTimeout(timeout);
        });
      } else {
        el.addEventListener('mouseenter', () => prefetchElement(el));
      }
    }
  }
}

/**
 * Manually prefetch a URL
 *
 * @param {string} url
 * @param {string|number} ttl - TTL string (e.g., "5m") or milliseconds
 * @returns {Promise<void>}
 */
export function prefetch(url, ttl = '5m') {
  const ttlMs = typeof ttl === 'string' ? parseDuration(ttl) : ttl;
  return prefetchURL(url, ttlMs);
}
